import { isGlobalViewer } from '../../../constants/roles';
// src/modules/goldVault/pages/TransferList.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, Button, Typography, Spin, Tag, Row, Col, Card, Select, Space,
  Statistic,
} from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getTransfers, getTransferSLA } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../context/AuthContext';
import { fetchBranchById } from '../../branches/api';

const { Title } = Typography;

const STATUS_COLOR = {
  CREATED: 'gold', APPROVED: 'blue', PACKED: 'cyan', DISPATCHED: 'orange',
  IN_TRANSIT: 'gold', RECEIVED: 'lime', VERIFIED: 'green', CLOSED: 'default',
  REJECTED: 'red',
};

const fmtW = (v) => `${Number(v || 0).toFixed(3)}g`;

export default function TransferList() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { user } = useAuth();

  const [transfers, setTransfers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 15, total: 0 });
  const [statusFilter, setStatusFilter] = useState('');
  const [sla, setSla] = useState(null);
  const [isHO, setIsHO] = useState(false);
  const [branchResolved, setBranchResolved] = useState(false);

  // Detect HO before first load
  useEffect(() => {
    const detect = async () => {
      if (!user) return;
      if (isGlobalViewer(user)) { setIsHO(true); setBranchResolved(true); return; }
      if (!user.branch) { setBranchResolved(true); return; }
      try {
        const res = await fetchBranchById(user.branch);
        const branch = res?.data || res;
        if (branch?.branchType === 'HO') setIsHO(true);
      } catch (_) {}
      setBranchResolved(true);
    };
    detect();
  }, [user]);

  useEffect(() => {
    if (branchResolved) { load(); loadSLA(); }
  }, [pagination.current, statusFilter, branchResolved]);

  const load = async () => {
    setLoading(true);
    const userBranchId = typeof user?.branch === 'object' ? user?.branch?._id : user?.branch;
    try {
      const res = await getTransfers({
        page: pagination.current,
        limit: pagination.pageSize,
        ...(statusFilter ? { status: statusFilter } : {}),
        // Non-HO users only see transfers involving their own branch
        ...(!isHO && userBranchId ? { branch: userBranchId } : {}),
      });
      setTransfers(res.data || []);
      setPagination((p) => ({ ...p, total: res.meta?.total || 0 }));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadSLA = async () => {
    try { const res = await getTransferSLA(); setSla(res.data); } catch {}
  };

  const columns = [
    {
      title: 'Transfer No.', dataIndex: 'transferNumber', key: 'transferNumber', width: 140,
      render: (v, r) => <Button type="link" size="small" onClick={() => navigate(`/vault/transfers/${r._id}`)}>{v}</Button>,
    },
    {
      title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v) => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
    },
    {
      title: 'Source Branch', key: 'sourceBranch', width: 130,
      render: (_, r) => r.sourceBranch?.name || '—',
    },
    {
      title: 'Cases', dataIndex: 'caseCount', key: 'caseCount', width: 70,
    },
    { title: 'Gross Wt', dataIndex: 'grossWeight', key: 'gw', width: 100, render: fmtW },
    { title: 'Net Wt', dataIndex: 'netWeight', key: 'nw', width: 100, render: fmtW },
    {
      title: 'Business Date', dataIndex: 'businessDate', key: 'bd', width: 120,
      render: (v) => v ? dayjs(v).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Created By', key: 'createdBy', width: 120,
      render: (_, r) => r.createdBy?.name || '—',
    },
    {
      title: 'Actions', key: 'actions', fixed: 'right', width: 100,
      render: (_, r) => (
        <Button size="small" onClick={() => navigate(`/vault/transfers/${r._id}`)}>View</Button>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col><Title level={4} style={{ margin: 0 }}>Inventory Transfers</Title></Col>
        <Col>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => { load(); loadSLA(); }}>Refresh</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/vault/transfers/create')}>
              New Transfer
            </Button>
          </Space>
        </Col>
      </Row>

      {sla && (
        <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
          {[
            { title: 'Pending Transfers', value: sla.pendingTransfers, color: undefined },
            { title: 'Overdue (>48h)', value: sla.overdueTransfers, valueStyle: { color: sla.overdueTransfers > 0 ? '#cf1322' : undefined } },
            { title: 'Avg Transfer Time', value: `${sla.averageTransferHours}h` },
          ].map((s) => (
            <Col xs={12} sm={6} key={s.title}>
              <Card size="small" bordered>
                <Statistic title={s.title} value={s.value} valueStyle={s.valueStyle || { fontSize: 18 }} />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      <Row style={{ marginBottom: 12 }}>
        <Col>
          <Select
            placeholder="All Statuses" allowClear style={{ width: 160 }}
            value={statusFilter || undefined}
            onChange={(v) => { setPagination((p) => ({ ...p, current: 1 })); setStatusFilter(v || ''); }}
          >
            {['CREATED', 'APPROVED', 'REJECTED', 'PACKED', 'DISPATCHED', 'IN_TRANSIT', 'RECEIVED', 'VERIFIED', 'CLOSED'].map((s) => (
              <Select.Option key={s} value={s}>{s}</Select.Option>
            ))}
          </Select>
        </Col>
      </Row>

      <Spin spinning={loading}>
        <Table
          dataSource={transfers}
          rowKey="_id"
          columns={columns}
          scroll={{ x: 1000 }}
          size="small"
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            onChange: (page, size) => setPagination({ current: page, pageSize: size, total: pagination.total }),
          }}
        />
      </Spin>
    </div>
  );
}
